(() => {
  'use strict';

  const body = document.body;
  if (!body) return;

  const recentEvents = new Map();
  const visibleTrackers = [];
  const trackedVisibleElements = new WeakSet();
  let analyticsGranted = false;
  let clarityEventsEnabled = false;
  let landingRecorded = false;

  const isPreview = body.dataset.clarityEnvironment !== 'live' || body.dataset.clarityDesignMode === 'true';
  const isSearchPage = body.dataset.clarityPageType === 'search';
  const isSensitivePage = body.dataset.claritySensitivePage === 'true';
  const clarityProjectId = String(body.dataset.clarityProjectId || '').replace(/[^a-z0-9]/gi, '');
  const trackingExcluded = isPreview || isSearchPage || isSensitivePage;
  const pendingEvents = [];
  const pendingEventLimit = 50;
  const pendingEventTtl = 120000;
  const privacyLoadMaxAttempts = 4;
  const privacyLoadWatchdog = 2000;
  const privacyLoadBackoff = [250, 500, 1000];
  let privacyState = trackingExcluded ? 'denied' : 'unresolved';
  let privacyLoadAttempts = 0;
  let privacyLoadInFlight = false;
  let privacyLoadAttemptToken = 0;
  let privacyLoadWatchdogTimer = null;
  let privacyLoadRetryTimer = null;
  let privacyDenialSent = false;

  const normalise = (value) => String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 255);

  const valueBucket = (value, ranges) => {
    const number = Number(value || 0);
    return ranges.find(([maximum]) => number <= maximum)?.[1] || ranges[ranges.length - 1][1];
  };

  const invokeClarity = (...args) => {
    if (typeof window.clarity !== 'function') {
      const queuedClarity = function () {
        queuedClarity.q.push(arguments);
      };
      queuedClarity.q = [];
      window.clarity = queuedClarity;
    }
    window.clarity(...args);
  };

  const ensureClarityLoader = () => {
    if (!clarityProjectId || document.querySelector(`script[src*="clarity.ms/tag/${clarityProjectId}"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${clarityProjectId}`;
    script.dataset.codexClarityLoader = 'true';
    document.head.appendChild(script);
  };

  const setTag = (key, value) => {
    if (!analyticsGranted) return;
    const cleanKey = normalise(key);
    const cleanValue = normalise(value);
    if (!cleanKey || !cleanValue) return;
    invokeClarity('set', cleanKey, cleanValue);
  };

  const deliverEvent = ({ name, tags, upgradeReason }) => {
    Object.entries(tags).forEach(([key, value]) => setTag(key, value));
    invokeClarity('event', name);
    if (upgradeReason) invokeClarity('upgrade', upgradeReason);
  };

  const discardExpiredPendingEvents = (now = Date.now()) => {
    while (pendingEvents.length && now - pendingEvents[0].queuedAt > pendingEventTtl) {
      pendingEvents.shift();
    }
  };

  const clearPendingEvents = () => {
    pendingEvents.length = 0;
  };

  const flushPendingEvents = () => {
    if (!clarityEventsEnabled) return;
    discardExpiredPendingEvents();
    while (pendingEvents.length) deliverEvent(pendingEvents.shift());
  };

  const emit = (name, tags = {}, dedupeWindow = 500, upgradeReason = '', queueWhileUnresolved = true) => {
    const canQueue = privacyState === 'unresolved' && queueWhileUnresolved;
    const canTrackPlausible = !isPreview
      && !isSensitivePage
      && typeof window.mochilaPlausible?.track === 'function';
    if (!clarityEventsEnabled && !canQueue && !canTrackPlausible) return false;

    const fingerprint = [
      name,
      tags.mch_action,
      tags.mch_operation,
      tags.mch_component,
      tags.mch_checkout_surface,
      tags.mch_checkout_method,
      tags.mch_product_id,
      tags.mch_error_kind,
      tags.mch_status_bucket
    ].map(normalise).join(':');
    const now = Date.now();
    if (now - (recentEvents.get(fingerprint) || 0) < dedupeWindow) return false;
    recentEvents.set(fingerprint, now);

    const plausibleTracked = canTrackPlausible
      ? window.mochilaPlausible.track(name, tags)
      : false;
    const pendingEvent = { name, tags, upgradeReason, queuedAt: now };
    if (clarityEventsEnabled) {
      deliverEvent(pendingEvent);
      return true;
    }

    discardExpiredPendingEvents(now);
    if (canQueue) {
      if (pendingEvents.length >= pendingEventLimit) pendingEvents.shift();
      pendingEvents.push(pendingEvent);
      return true;
    }
    return plausibleTracked;
  };

  const setPageContext = () => {
    setTag('mch_storefront_build', body.dataset.clarityStorefrontBuild);
    setTag('mch_theme_version', body.dataset.clarityThemeVersion);
    setTag('mch_theme_id', body.dataset.clarityThemeId);
    setTag('mch_theme_name', body.dataset.clarityThemeName);
    setTag('mch_theme_role', body.dataset.clarityThemeRole);
    setTag('mch_release_id', body.dataset.clarityReleaseId);
    setTag('mch_page_type', body.dataset.clarityPageType);
    setTag('mch_template', [body.dataset.clarityTemplate, body.dataset.clarityTemplateSuffix].filter(Boolean).join('.'));
    setTag('mch_locale', body.dataset.clarityLocale);
    setTag('mch_market', body.dataset.clarityMarket);
    setTag('mch_currency', body.dataset.clarityCurrency);
    setTag('mch_collection_handle', body.dataset.clarityCollection);
    setTag('mch_product_handle', body.dataset.clarityProduct);
    setTag('mch_environment', body.dataset.clarityEnvironment);
    setTag('mch_viewport', window.matchMedia('(max-width: 749px)').matches ? 'mobile' : window.matchMedia('(max-width: 1023px)').matches ? 'tablet' : 'desktop');

  };

  const recordLanding = () => {
    if (landingRecorded || !clarityEventsEnabled) return;

    const referrer = document.referrer ? new URL(document.referrer, window.location.href) : null;
    if (referrer && referrer.hostname === window.location.hostname) return;

    const params = new URLSearchParams(window.location.search);
    const safeCampaignValue = (key) => {
      const value = (params.get(key) || '').toLowerCase();
      return /^[a-z0-9_.-]{1,40}$/.test(value) ? value : '';
    };

    landingRecorded = emit('mochila_traffic_landing', {
      mch_landing_type: referrer ? 'external-referral' : 'direct',
      mch_utm_source: safeCampaignValue('utm_source'),
      mch_utm_medium: safeCampaignValue('utm_medium')
    }, 0);
  };

  const tryVisibleTracker = (tracker) => {
    if (tracker.seen || !clarityEventsEnabled) return;
    const rect = tracker.element.getBoundingClientRect();
    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    if (rect.height <= 0 || visibleHeight / rect.height < tracker.threshold) return;

    const tags = typeof tracker.tags === 'function' ? tracker.tags(tracker.element) : tracker.tags;
    if (emit(tracker.eventName, tags, 0, '', false)) {
      tracker.seen = true;
      tracker.observer?.unobserve(tracker.element);
    }
  };

  const refreshVisibleTrackers = () => visibleTrackers.forEach(tryVisibleTracker);

  const trackMeaningfulView = (selector, eventName, tags, threshold = 0.5) => {
    document.querySelectorAll(selector).forEach((element) => {
      if (trackedVisibleElements.has(element)) return;
      trackedVisibleElements.add(element);
      const tracker = { element, eventName, tags, threshold, seen: false, observer: null };
      if ('IntersectionObserver' in window) {
        tracker.observer = new IntersectionObserver(() => tryVisibleTracker(tracker), { threshold });
        tracker.observer.observe(element);
      }
      visibleTrackers.push(tracker);
    });
  };

  const cancelPrivacyLoadWork = () => {
    if (privacyLoadWatchdogTimer) clearTimeout(privacyLoadWatchdogTimer);
    if (privacyLoadRetryTimer) clearTimeout(privacyLoadRetryTimer);
    privacyLoadWatchdogTimer = null;
    privacyLoadRetryTimer = null;
    privacyLoadInFlight = false;
    privacyLoadAttemptToken += 1;
  };

  const denyClarity = (reload = false) => {
    const shouldSignalDenial = analyticsGranted || !privacyDenialSent;
    analyticsGranted = false;
    clarityEventsEnabled = false;
    privacyState = 'denied';
    clearPendingEvents();
    recentEvents.clear();
    if (shouldSignalDenial) {
      invokeClarity('consentv2', {
        ad_Storage: 'denied',
        analytics_Storage: 'denied'
      });
      privacyDenialSent = true;
    }
    if (reload) window.location.reload();
  };

  const customerPrivacyReady = () => (
    typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function'
  );

  const syncClarityConsent = () => {
    if (trackingExcluded) {
      cancelPrivacyLoadWork();
      denyClarity();
      return;
    }

    const privacy = window.Shopify?.customerPrivacy;
    if (!customerPrivacyReady()) {
      cancelPrivacyLoadWork();
      denyClarity(analyticsGranted);
      return;
    }

    cancelPrivacyLoadWork();
    const analytics = Boolean(privacy.analyticsProcessingAllowed());
    const marketing = typeof privacy.marketingAllowed === 'function' && Boolean(privacy.marketingAllowed());
    const analyticsWasGranted = analyticsGranted;

    if (!analytics) {
      denyClarity(analyticsWasGranted);
      return;
    }

    invokeClarity('consentv2', {
      ad_Storage: marketing ? 'granted' : 'denied',
      analytics_Storage: 'granted'
    });
    ensureClarityLoader();
    analyticsGranted = true;
    clarityEventsEnabled = true;
    privacyState = 'granted';
    privacyDenialSent = false;
    setPageContext();
    flushPendingEvents();
    recordLanding();
    refreshVisibleTrackers();
  };

  const finalisePrivacyLoadFailure = () => {
    cancelPrivacyLoadWork();
    denyClarity();
  };

  const schedulePrivacyLoadRetry = () => {
    if (privacyState !== 'unresolved' || privacyLoadRetryTimer) return;
    if (privacyLoadAttempts >= privacyLoadMaxAttempts) {
      finalisePrivacyLoadFailure();
      return;
    }

    const delay = privacyLoadBackoff[Math.min(privacyLoadAttempts - 1, privacyLoadBackoff.length - 1)];
    privacyLoadRetryTimer = setTimeout(() => {
      privacyLoadRetryTimer = null;
      loadCustomerPrivacy();
    }, delay);
  };

  const loadCustomerPrivacy = (finalIfUnavailable = false) => {
    if (customerPrivacyReady()) {
      syncClarityConsent();
      return;
    }

    if (trackingExcluded) {
      denyClarity();
      return;
    }

    if (privacyLoadInFlight || privacyLoadRetryTimer || privacyState !== 'unresolved') return;

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      if (finalIfUnavailable) finalisePrivacyLoadFailure();
      return;
    }

    if (privacyLoadAttempts >= privacyLoadMaxAttempts) {
      finalisePrivacyLoadFailure();
      return;
    }

    privacyLoadAttempts += 1;
    privacyLoadInFlight = true;
    const attemptToken = ++privacyLoadAttemptToken;
    const finishAttempt = (error) => {
      if (!privacyLoadInFlight || attemptToken !== privacyLoadAttemptToken) return;
      if (privacyLoadWatchdogTimer) clearTimeout(privacyLoadWatchdogTimer);
      privacyLoadWatchdogTimer = null;
      privacyLoadInFlight = false;

      if (!error && customerPrivacyReady()) {
        syncClarityConsent();
        return;
      }
      schedulePrivacyLoadRetry();
    };

    privacyLoadWatchdogTimer = setTimeout(() => {
      finishAttempt(new Error('Customer Privacy feature load timed out'));
    }, privacyLoadWatchdog);

    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        finishAttempt
      );
    } catch (error) {
      finishAttempt(error);
    }
  };

  /*
   * Keep this recovery path active even after a bounded feature-load failure:
   * Shopify can dispatch visitorConsentCollected after another consent surface
   * has made the Customer Privacy API available.
   */
  const recoverClarityConsent = () => {
    /*
     * A consent notification is not retroactive. If it arrives before the
     * initial privacy check finishes, discard interactions observed before the
     * visitor's explicit choice instead of flushing them after that choice.
     */
    if (privacyState === 'unresolved') {
      clearPendingEvents();
      recentEvents.clear();
    }

    if (customerPrivacyReady()) {
      syncClarityConsent();
      return;
    }

    cancelPrivacyLoadWork();
    if (analyticsGranted) {
      denyClarity(true);
      return;
    }

    privacyState = trackingExcluded ? 'denied' : 'unresolved';
    privacyLoadAttempts = 0;
    loadCustomerPrivacy(true);
  };

  /*
   * Excluded pages fail closed immediately, before any event handlers can add
   * observations to the unresolved-consent queue.
   */
  if (trackingExcluded) {
    denyClarity();
  }

  const productContext = (element) => {
    const card = element?.closest('product-block, [data-product-id]');
    if (!card) return {};

    const cards = Array.from(document.querySelectorAll('#product-list product-block, [data-product-list] product-block'));
    const position = cards.indexOf(card);

    return {
      mch_product_id: card.dataset.productId,
      mch_product_handle: card.dataset.productHandle,
      mch_product_position: position >= 0 ? position + 1 : ''
    };
  };

  const matchInPath = (event, selector) => event.composedPath()
    .find((node) => node instanceof Element && node.matches(selector));

  const checkoutSurface = (element) => element.closest('.cart-drawer')
    ? 'cart-drawer'
    : element.closest('.product-form, .template-product')
      ? 'pdp'
      : 'cart-page';

  const emitCheckoutIntent = (element, method, signal) => emit('mochila_checkout_intent', {
    mch_checkout_surface: checkoutSurface(element),
    mch_checkout_method: method,
    mch_checkout_signal: signal
  }, 1500, 'checkout-intent');

  const eventWasCancelled = (event) => event.defaultPrevented || event.returnValue === false;

  const emitStandardCheckoutAfterDispatch = (event, element, signal) => {
    queueMicrotask(() => {
      if (!eventWasCancelled(event)) emitCheckoutIntent(element, 'standard', signal);
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const advertorialCta = target.closest('[data-advertorial-cta]');
    if (advertorialCta) {
      emit('mochila_advertorial_cta_click', {
        mch_action: advertorialCta.dataset.advertorialCta,
        mch_component: advertorialCta.dataset.advertorialPlacement || 'story'
      });
      return;
    }

    const categoryLink = target.closest('.codex-category-nav__link');
    if (categoryLink) {
      emit('mochila_category_navigation', {
        mch_from_collection: body.dataset.clarityCollection || 'none',
        mch_to_collection: categoryLink.getAttribute('href')?.split('/').filter(Boolean).pop()
      });
      return;
    }

    const productLink = target.closest('#product-list product-block .product-link, [data-product-list] product-block .product-link');
    if (productLink) {
      emit('mochila_collection_card_click', {
        ...productContext(productLink),
        mch_card_surface: productLink.closest('.image-cont') ? 'image' : 'details'
      });
      return;
    }

    const galleryControl = target.closest('.gallery .show-gallery');
    if (galleryControl) {
      emit('mochila_pdp_gallery', {
        mch_action: 'zoom'
      });
      return;
    }

    const reviewControl = target.closest('[data-codex-shop-review-summary], [data-codex-review-previous], [data-codex-review-next], a[href*="happy-customers"], a[href="#product-reviews"], .jdgm-write-rev-link, .jdgm-row-readmore .jdgm-btn, .jdgm-rev-carousel-prev, .jdgm-rev-carousel-next, .jdgm-paginate__page');
    if (reviewControl) {
      const reviewAction = reviewControl.matches('.jdgm-write-rev-link')
        ? 'write-review'
        : reviewControl.matches('.jdgm-row-readmore .jdgm-btn, .jdgm-paginate__page')
          ? 'load-more'
          : reviewControl.matches('[data-codex-review-previous], [data-codex-review-next], .jdgm-rev-carousel-prev, .jdgm-rev-carousel-next')
            ? 'proof-carousel'
            : 'open-reviews';
      emit('mochila_reviews_interaction', {
        mch_action: reviewAction
      });
      return;
    }

    if (target.closest('.codex-artisan-story summary, .codex-artisan-story__link, a[href*="/pages/artisans"]')) {
      emit('mochila_artisan_story', { mch_action: 'directory-or-details' });
      return;
    }

    const checkout = matchInPath(event, 'a[href*="/checkout"], .additional-checkout-buttons input, .additional-checkout-buttons button, shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .shopify-payment-button__button');
    if (checkout) {
      const checkoutIsExpress = Boolean(
        checkout.closest('.additional-checkout-buttons') ||
        checkout.matches('shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .shopify-payment-button__button')
      );
      if (checkoutIsExpress) {
        emitCheckoutIntent(checkout, 'express', 'click');
      } else {
        emitStandardCheckoutAfterDispatch(event, checkout, 'click');
      }
      return;
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target : null;
    if (!form) return;

    if (form.matches('[data-codex-search-form], form[action*="/search"]')) {
      const queryLength = (form.querySelector('input[type="search"], input[name="q"]')?.value || '').trim().length;
      emit('mochila_search_submit', {
        mch_query_length: valueBucket(queryLength, [[0, '0'], [10, '1-10'], [30, '11-30'], [Number.POSITIVE_INFINITY, '31-plus']])
      });
    }

    const submitter = event.submitter instanceof Element ? event.submitter : null;
    if (submitter?.matches('[name="checkout"], .codex-cart-checkout')) {
      emitStandardCheckoutAfterDispatch(event, form, 'submit');
    }
  }, true);

  document.addEventListener('mochila:pdp:gallery-change', (event) => {
    const detail = event.detail || {};
    emit('mochila_pdp_gallery', {
      mch_action: 'change',
      mch_gallery_method: detail.method,
      mch_media_type: detail.mediaType,
      mch_media_position: detail.position
    });
  });

  document.addEventListener('mochila:size-guide:interaction', (event) => {
    const detail = event.detail || {};
    emit('mochila_size_guide', {
      mch_action: detail.action,
      mch_size_choice: detail.choice,
      mch_size_method: detail.method,
      mch_size_available: detail.available
    });
  });

  document.addEventListener('mochila:reviews:interaction', (event) => {
    const detail = event.detail || {};
    emit('mochila_reviews_interaction', {
      mch_action: detail.action,
      mch_review_surface: detail.surface
    });
  });

  document.addEventListener('mochila:cart:add-success', (event) => {
    const detail = event.detail || {};
    const tags = {
      mch_add_origin: detail.origin,
      mch_promo_context: detail.promoContext,
      mch_product_id: detail.productId,
      mch_product_handle: detail.productHandle,
      mch_variant_id: detail.variantId,
      mch_quantity: valueBucket(detail.quantity, [[1, '1'], [2, '2'], [Number.POSITIVE_INFINITY, '3-plus']])
    };

    if (detail.giftWrap) {
      emit('mochila_gift_wrap_add_success', tags);
      return;
    }

    emit('mochila_cart_add_success', tags, 500, 'add-to-cart');

    if (detail.promoContext === 'second-bag') {
      emit('mochila_second_bag_offer_accept', tags);
    }
  });

  document.addEventListener('mochila:cart:drawer-open', (event) => {
    const detail = event.detail || {};
    emit('mochila_cart_drawer_open', {
      mch_drawer_reason: detail.reason || 'other'
    });
  });

  document.addEventListener('mochila:cart:second-bag-offer-view', () => {
    emit('mochila_second_bag_offer_view', { mch_offer_surface: 'cart-drawer' }, 30000);
  });

  document.addEventListener('mochila:cart:promo-applied', () => {
    emit('mochila_second_bag_offer_applied', { mch_offer_surface: 'cart' }, 30000);
  });

  document.addEventListener('mochila:cart:gift-note-saved', (event) => {
    const detail = event.detail || {};
    emit('mochila_gift_note_saved', {
      mch_note_state: detail.state,
      mch_note_length: detail.lengthBucket
    });
  });

  document.addEventListener('mochila:ui-error', (event) => {
    const detail = event.detail || {};
    emit('mochila_ui_error', {
      mch_component: detail.component,
      mch_operation: detail.operation,
      mch_error_kind: detail.kind,
      mch_status_bucket: valueBucket(detail.status, [[0, 'none'], [399, '3xx'], [499, '4xx'], [599, '5xx'], [Number.POSITIVE_INFINITY, 'other']])
    }, 30000);
  });

  trackMeaningfulView('.codex-size-guide__header, .codex-size-guide--product', 'mochila_size_guide', { mch_action: 'view' });
  trackMeaningfulView('.codex-artisan-story__intro', 'mochila_artisan_story', { mch_action: 'view' });
  trackMeaningfulView(
    '[data-advertorial-story]',
    'mochila_advertorial_view',
    (element) => ({ mch_component: element.dataset.advertorialVariant || 'story' })
  );
  trackMeaningfulView('[data-advertorial-shelf]', 'mochila_advertorial_shelf_view', { mch_component: 'available-products' });

  document.addEventListener('visitorConsentCollected', recoverClarityConsent);
  window.addEventListener('PandectesEvent_OnConsent', recoverClarityConsent);
  if (document.readyState === 'complete') {
    loadCustomerPrivacy(true);
  } else {
    window.addEventListener('load', () => loadCustomerPrivacy(true), { once: true });
  }
  loadCustomerPrivacy();
})();
